import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import { useInvalidateCredits } from '@/features/dashboard/hooks/useCredits';
import { toast } from 'sonner';

// =========================================
// Types
// =========================================

export interface ChatMessage {
  id: string;
  user_id: string;
  source_id: string;
  role: 'user' | 'assistant';
  content: string;
  cited_passages: CitedPassage[];
  created_at: string;
}

export interface CitedPassage {
  text: string;
  relevance: string;
}

interface SendMessageInput {
  source_id: string;
  message: string;
  ai_model?: 'gpt-4-turbo' | 'claude-3.5-sonnet';
}

const CHAT_KEY = 'chat-messages';

// =========================================
// Queries
// =========================================

/** Fetch all chat messages for a source */
export function useChatMessages(sourceId: string | undefined) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [CHAT_KEY, sourceId, user?.id],
    queryFn: async (): Promise<ChatMessage[]> => {
      if (!sourceId) return [];

      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('source_id', sourceId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data ?? []) as ChatMessage[];
    },
    enabled: !!sourceId && !!user,
  });
}

// =========================================
// Mutations
// =========================================

/** Send a chat message and get AI response */
export function useSendMessage() {
  const queryClient = useQueryClient();
  const invalidateCredits = useInvalidateCredits();

  return useMutation({
    mutationFn: async (input: SendMessageInput): Promise<ChatMessage> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('chat-with-source', {
        body: {
          source_id: input.source_id,
          message: input.message,
          ai_model: input.ai_model || 'gpt-4-turbo',
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Chat request failed');
      }

      const result = response.data as { success: boolean; message: ChatMessage; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Chat request failed');
      }

      return result.message;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [CHAT_KEY, variables.source_id],
      });
      invalidateCredits();
    },
    onError: (error: Error) => {
      if (error.message.includes('Insufficient credits')) {
        toast.error('Insufficient credits. Purchase more to continue chatting.');
      } else {
        toast.error(`Chat failed: ${error.message}`);
      }
    },
  });
}

/** Delete all chat messages for a source */
export function useClearChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sourceId: string): Promise<void> => {
      const { error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('source_id', sourceId);

      if (error) throw error;
    },
    onSuccess: (_data, sourceId) => {
      queryClient.invalidateQueries({
        queryKey: [CHAT_KEY, sourceId],
      });
      toast.success('Chat history cleared');
    },
    onError: () => {
      toast.error('Failed to clear chat history');
    },
  });
}
